import { useState, useEffect } from 'react';
import { useReadContracts } from 'wagmi';
import { contractAddress } from '../config/contract';
import { abi } from '../abi';
import { abiBSC } from '../abiBSC';
import { formatUnits } from 'viem';
import { supabase } from '../services/supabase';

// Hardcap in USD
const HARDCAP = 200000; // $ hardcap

// Legacy contract address for data reading only
const LEGACY_ETH_CONTRACT = '0xf819158094b3eE129e7721A9358f930AB794e7B9';

export function useTotalRaised() {
  const [totalRaised, setTotalRaised] = useState<number | null>(null);
  const [percentage, setPercentage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [solanaTotal, setSolanaTotal] = useState(0); // This now represents Solana + BSC
  const [bscTotal, setBscTotal] = useState(0); // BSC only
  const [truesolanaTotal, setTrueSolanaTotal] = useState(0); // Solana only
  const [evmTotal, setEvmTotal] = useState(0); // Ethereum only
  const [legacyEvmTotal, setLegacyEvmTotal] = useState(0); // Legacy Ethereum contract

  // Get all contract data at once for better reliability
  const contractReads = useReadContracts({
    contracts: [
      // Current Ethereum values
      {
        address: contractAddress.eth,
        abi,
        functionName: 'getTotalRaisedUSD',
        chainId: 1
      },
      {
        address: contractAddress.eth,
        abi,
        functionName: 'totalRaisedETHinUSD',
        chainId: 1
      },
      {
        address: contractAddress.eth,
        abi,
        functionName: 'totalRaisedUSDT',
        chainId: 1
      },
      {
        address: contractAddress.eth,
        abi,
        functionName: 'totalRaisedUSDC',
        chainId: 1
      },
      // BSC values
      {
        address: contractAddress.bsc,
        abi: abiBSC,
        functionName: 'getTotalRaisedUSD',
        chainId: 56
      },
      {
        address: contractAddress.bsc,
        abi: abiBSC,
        functionName: 'totalRaisedBNBinUSD',
        chainId: 56
      },
      {
        address: contractAddress.bsc,
        abi: abiBSC,
        functionName: 'totalRaisedUSDT',
        chainId: 56
      },
      {
        address: contractAddress.bsc,
        abi: abiBSC,
        functionName: 'totalRaisedUSDC',
        chainId: 56
      },
      // Legacy Ethereum values - read only
      {
        address: LEGACY_ETH_CONTRACT,
        abi,
        functionName: 'getTotalRaisedUSD',
        chainId: 1
      },
      {
        address: LEGACY_ETH_CONTRACT,
        abi,
        functionName: 'totalRaisedETHinUSD',
        chainId: 1
      },
      {
        address: LEGACY_ETH_CONTRACT,
        abi,
        functionName: 'totalRaisedUSDT',
        chainId: 1
      },
      {
        address: LEGACY_ETH_CONTRACT,
        abi,
        functionName: 'totalRaisedUSDC',
        chainId: 1
      }
    ]
  });

  // Get Solana purchases
  useEffect(() => {
    const fetchSolanaPurchases = async () => {
      try {
        const { data: solanaPurchases, error: solanaError } = await supabase
          .from('solana_purchases')
          .select('*');
        
        if (solanaError) {
          console.error("Error fetching Solana purchases:", solanaError);
          return 0;
        } 
        
        if (solanaPurchases && solanaPurchases.length > 0) {
          const solTotal = solanaPurchases.reduce((sum, purchase) => 
            sum + (purchase.amount_usd || 0), 0);
          
          // Store the true Solana total
          setTrueSolanaTotal(solTotal);
          
          // Calculate BSC total from individual values
          let bscAmount = 0;
          let bnbRaised = 0;
          let bscUsdtRaised = 0;
          let bscUsdcRaised = 0;
          
          // Get BNB raised amount
          if (contractReads.data && contractReads.data[5] && contractReads.data[5].status === 'success') {
            const rawBNBValue = contractReads.data[5].result as bigint;
            bnbRaised = Number(formatUnits(rawBNBValue, 18));
            bscAmount += bnbRaised;
          }
          
          // Get BSC USDT raised amount
          if (contractReads.data && contractReads.data[6] && contractReads.data[6].status === 'success') {
            const rawBSCUSDTValue = contractReads.data[6].result as bigint;
            bscUsdtRaised = Number(formatUnits(rawBSCUSDTValue, 6)); // USDT uses 6 decimals
            bscAmount += bscUsdtRaised;
          }
          
          // Get BSC USDC raised amount
          if (contractReads.data && contractReads.data[7] && contractReads.data[7].status === 'success') {
            const rawBSCUSDCValue = contractReads.data[7].result as bigint;
            bscUsdcRaised = Number(formatUnits(rawBSCUSDCValue, 6)); // USDC uses 6 decimals
            bscAmount += bscUsdcRaised;
          }
          
          // Verify against BSC getTotalRaisedUSD if available
          if (contractReads.data && contractReads.data[4] && contractReads.data[4].status === 'success') {
            const bscContractTotal = Number(formatUnits(contractReads.data[4].result as bigint, 18));
            
            // Always use the contract's value as the source of truth for BSC
              if (bscContractTotal > 0) {
              console.log("Using BSC contract's getTotalRaisedUSD value:", bscContractTotal);
                bscAmount = bscContractTotal;
            } else if (Math.abs(bscContractTotal - bscAmount) > 1) {
              // Just log a warning for debugging but still use contract value
              console.warn("BSC: Discrepancy between sum of individual assets and getTotalRaisedUSD", {
                calculatedSum: bscAmount,
                contractTotal: bscContractTotal
              });
            }
          }
          
          // Store BSC total
          setBscTotal(bscAmount);
          
          // For backward compatibility, still set the combined total
          const finalTotal = solTotal + bscAmount;
          setSolanaTotal(finalTotal);
          return finalTotal;
        } else {
          setTrueSolanaTotal(0);
          
          // If no Solana purchases, calculate BSC amount from individual values
          let bscAmount = 0;
          let bnbRaised = 0;
          let bscUsdtRaised = 0;
          let bscUsdcRaised = 0;
          
          // Get BNB raised amount
          if (contractReads.data && contractReads.data[5] && contractReads.data[5].status === 'success') {
            const rawBNBValue = contractReads.data[5].result as bigint;
            bnbRaised = Number(formatUnits(rawBNBValue, 18));
            bscAmount += bnbRaised;
          }
          
          // Get BSC USDT raised amount
          if (contractReads.data && contractReads.data[6] && contractReads.data[6].status === 'success') {
            const rawBSCUSDTValue = contractReads.data[6].result as bigint;
            bscUsdtRaised = Number(formatUnits(rawBSCUSDTValue, 6)); // USDT uses 6 decimals
            bscAmount += bscUsdtRaised;
          }
          
          // Get BSC USDC raised amount
          if (contractReads.data && contractReads.data[7] && contractReads.data[7].status === 'success') {
            const rawBSCUSDCValue = contractReads.data[7].result as bigint;
            bscUsdcRaised = Number(formatUnits(rawBSCUSDCValue, 6)); // USDC uses 6 decimals
            bscAmount += bscUsdcRaised;
          }
          
          // Verify against BSC getTotalRaisedUSD if available
          if (contractReads.data && contractReads.data[4] && contractReads.data[4].status === 'success') {
            const bscContractTotal = Number(formatUnits(contractReads.data[4].result as bigint, 18));
            
            // Always use the contract's value as the source of truth for BSC
              if (bscContractTotal > 0) {
              console.log("Using BSC contract's getTotalRaisedUSD value:", bscContractTotal);
                bscAmount = bscContractTotal;
            } else if (Math.abs(bscContractTotal - bscAmount) > 1) {
              // Just log a warning for debugging but still use contract value
              console.warn("BSC: Discrepancy between sum of individual assets and getTotalRaisedUSD", {
                calculatedSum: bscAmount,
                contractTotal: bscContractTotal
              });
            }
          }
          
          setBscTotal(bscAmount);
          setSolanaTotal(bscAmount); // For backward compatibility
          return bscAmount;
        }
      } catch (solError) {
        console.error("Error in Solana purchase processing:", solError);
        setTrueSolanaTotal(0);
        
        // If error occurred, still try to calculate BSC total
        let bscAmount = 0;
        
        if (contractReads.data && contractReads.data[4] && contractReads.data[4].status === 'success') {
          bscAmount = Number(formatUnits(contractReads.data[4].result as bigint, 18));
          console.log("Using BSC contract's getTotalRaisedUSD as the source of truth:", bscAmount);
          setBscTotal(bscAmount);
          setSolanaTotal(bscAmount); // For backward compatibility
          return bscAmount;
        }
        
        setBscTotal(0);
        return 0;
      }
    };

    fetchSolanaPurchases();
  }, [contractReads.data]); // Update when contract data changes

  // Legacy Ethereum contract data calculation
  useEffect(() => {
    const fetchLegacyData = async () => {
      try {
        let legacyTotal = 0;
        let legacyEthRaised = 0;
        let legacyUsdtRaised = 0;
        let legacyUsdcRaised = 0;
        
        // Get Legacy ETH raised amount (index 9)
        if (contractReads.data && contractReads.data[9] && contractReads.data[9].status === 'success') {
          const rawLegacyETHValue = contractReads.data[9].result as bigint;
          legacyEthRaised = Number(formatUnits(rawLegacyETHValue, 18));
          legacyTotal += legacyEthRaised;
        }
        
        // Get Legacy USDT raised amount (index 10)
        if (contractReads.data && contractReads.data[10] && contractReads.data[10].status === 'success') {
          const rawLegacyUSDTValue = contractReads.data[10].result as bigint;
          // Contract normalizes all values to 18 decimals
          legacyUsdtRaised = Number(formatUnits(rawLegacyUSDTValue, 18));
          legacyTotal += legacyUsdtRaised;
        }
        
        // Get Legacy USDC raised amount (index 11)
        if (contractReads.data && contractReads.data[11] && contractReads.data[11].status === 'success') {
          const rawLegacyUSDCValue = contractReads.data[11].result as bigint;
          // Contract normalizes all values to 18 decimals
          legacyUsdcRaised = Number(formatUnits(rawLegacyUSDCValue, 18));
          legacyTotal += legacyUsdcRaised;
        }
        
        // Verify against Legacy getTotalRaisedUSD if available
        let finalLegacyTotal = legacyTotal;
        if (contractReads.data && contractReads.data[8] && contractReads.data[8].status === 'success') {
          const rawLegacyContractTotal = contractReads.data[8].result as bigint;
          const legacyContractTotal = Number(formatUnits(rawLegacyContractTotal, 18));
          
          // If there's a significant difference, use contract total
          if (Math.abs(legacyContractTotal - legacyTotal) > 1 && legacyContractTotal > 0) {
            finalLegacyTotal = legacyContractTotal;
          }
        }
        
        console.log("Legacy ETH contract data:", {
          ethRaised: legacyEthRaised,
          usdtRaised: legacyUsdtRaised,
          usdcRaised: legacyUsdcRaised,
          calculated: legacyTotal,
          contractTotal: finalLegacyTotal
        });
        
        // Store the Legacy Ethereum total
        setLegacyEvmTotal(finalLegacyTotal);
        
      } catch (err) {
        console.error("Error calculating legacy total:", err);
        setLegacyEvmTotal(0);
      }
    };
    
    fetchLegacyData();
  }, [contractReads.data]);

  // EVM (Ethereum) purchase retrieval and total calculation
  useEffect(() => {
    const fetchTotalRaised = async () => {
      try {
        setLoading(true);
        
        // Calculate Ethereum total by summing the individual amounts
        let ethTotal = 0;
        let ethRaised = 0;
        let usdtRaised = 0;
        let usdcRaised = 0;
        
        // Get ETH raised amount
        if (contractReads.data && contractReads.data[1] && contractReads.data[1].status === 'success') {
          const rawETHValue = contractReads.data[1].result as bigint;
          ethRaised = Number(formatUnits(rawETHValue, 18));
          ethTotal += ethRaised;
        }
        
        // Get USDT raised amount
        if (contractReads.data && contractReads.data[2] && contractReads.data[2].status === 'success') {
          const rawUSDTValue = contractReads.data[2].result as bigint;
          // Contract now normalizes all values to 18 decimals
          usdtRaised = Number(formatUnits(rawUSDTValue, 18)); 
          ethTotal += usdtRaised;
        }
        
        // Get USDC raised amount
        if (contractReads.data && contractReads.data[3] && contractReads.data[3].status === 'success') {
          const rawUSDCValue = contractReads.data[3].result as bigint;
          // Contract now normalizes all values to 18 decimals
          usdcRaised = Number(formatUnits(rawUSDCValue, 18)); 
          ethTotal += usdcRaised;
        }
        
        // Verify against getTotalRaisedUSD if available, but prioritize our own calculation
        let finalEthTotal = ethTotal;
        if (contractReads.data && contractReads.data[0] && contractReads.data[0].status === 'success') {
          const rawContractTotal = contractReads.data[0].result as bigint;
          const contractTotal = Number(formatUnits(rawContractTotal, 18));
          
          // If there's a significant difference, log a warning
          if (Math.abs(contractTotal - ethTotal) > 1) {
            // Only use contract total if our calculation is zero
            if (ethTotal === 0 && contractTotal > 0) {
              finalEthTotal = contractTotal;
            }
          }
        }
        
        // Store the current Ethereum total
        setEvmTotal(finalEthTotal);
        
        // Use Solana + BSC sum
        const finalSolanaAndBsc = solanaTotal || 0;
        
        // Add current Ethereum, legacy Ethereum, and (Solana+BSC) totals
        const combinedTotal = finalEthTotal + legacyEvmTotal + finalSolanaAndBsc;
        
        setTotalRaised(combinedTotal);
        
        // Calculate percentage
        const progressPercentage = Math.min((combinedTotal / HARDCAP) * 100, 100);
        setPercentage(progressPercentage);
        
        setError(null);
      } catch (err) {
        console.error("Error calculating total raised:", err);
        setError('Failed to fetch total raised amount');
        
        // Fallback calculation if main calculation fails
        try {
          let ethTotalFallback = 0;
          
          // Try to calculate by summing individual purchase methods if available
          if (contractReads.data) {
            if (contractReads.data[1] && contractReads.data[1].status === 'success') {
              ethTotalFallback += Number(formatUnits(contractReads.data[1].result as bigint, 18));
            }
            
            if (contractReads.data[2] && contractReads.data[2].status === 'success') {
              ethTotalFallback += Number(formatUnits(contractReads.data[2].result as bigint, 18));
            }
            
            if (contractReads.data[3] && contractReads.data[3].status === 'success') {
              ethTotalFallback += Number(formatUnits(contractReads.data[3].result as bigint, 18));
            }
            
            console.log("Calculated ETH total fallback:", ethTotalFallback);
          }
          
          if (ethTotalFallback > 0) {
            setEvmTotal(ethTotalFallback);
            setTotalRaised(ethTotalFallback + legacyEvmTotal + (solanaTotal || 0));
            const progressPercentage = Math.min(((ethTotalFallback + legacyEvmTotal + (solanaTotal || 0)) / HARDCAP) * 100, 100);
            setPercentage(progressPercentage);
          } else if (contractReads.data && contractReads.data[0] && contractReads.data[0].status === 'success') {
            // Fall back to main total if available
            const mainTotal = Number(formatUnits(contractReads.data[0].result as bigint, 18));
            setEvmTotal(mainTotal);
            setTotalRaised(mainTotal + legacyEvmTotal + (solanaTotal || 0));
            const progressPercentage = Math.min(((mainTotal + legacyEvmTotal + (solanaTotal || 0)) / HARDCAP) * 100, 100);
            setPercentage(progressPercentage);
          } else {
            // Last resort - use only Legacy + Solana+BSC total
            setEvmTotal(0);
            setTotalRaised(legacyEvmTotal + (solanaTotal || 0));
            setPercentage(Math.min(((legacyEvmTotal + (solanaTotal || 0)) / HARDCAP) * 100, 100));
          }
        } catch (fallbackErr) {
          console.error("Error in fallback calculation:", fallbackErr);
          // In case of complete failure, set to default values
          setEvmTotal(0);
          setTotalRaised(legacyEvmTotal + (solanaTotal || 0));
          setPercentage(Math.min(((legacyEvmTotal + (solanaTotal || 0)) / HARDCAP) * 100, 100));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTotalRaised();
  }, [contractReads.data, solanaTotal, bscTotal, truesolanaTotal, legacyEvmTotal]);

  return { 
    totalRaised, 
    hardcap: HARDCAP, 
    percentage, 
    loading, 
    error,
    solanaTotal,       // For backward compatibility, this is still Solana + BSC
    evmTotal,          // This represents only current Ethereum
    legacyEvmTotal,    // This represents only legacy Ethereum contract
    bscTotal,          // This represents only BSC
    truesolanaTotal    // This represents only Solana
  };
} 